import React, { useState, useEffect } from 'react';
import { useTCoins, TCOINS_TYPES, EXCHANGE_RATE } from '../../context/TCoinsContext';
import { useAuth } from '../../context/AuthContext';
import { collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const CoinsExchange = ({ className = "" }) => {
  const { currentUser } = useAuth();
  const { tcoins, getTotalReal, getExchangeValue, loadTCoins } = useTCoins();
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [exchangeHistory, setExchangeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadTCoins(currentUser.uid);
      loadExchangeHistory();
    }
  }, [currentUser]);

  const loadExchangeHistory = async () => {
    if (!currentUser) return;
    
    try {
      const exchangeRef = collection(db, 'exchangeRequests');
      const q = query(
        exchangeRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const history = [];
      snapshot.forEach((doc) => {
        history.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setExchangeHistory(history);
    } catch (error) {
      console.error('Error loading exchange history:', error);
    }
  };

  const handleExchangeRequest = async () => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบก่อน');
      return;
    }

    const amount = parseInt(exchangeAmount);
    if (!amount || amount <= 0) {
      alert('กรุณากรอกจำนวนเหรียญที่ถูกต้อง');
      return;
    }

    if (amount < EXCHANGE_RATE.MIN_WITHDRAWAL) {
      alert(`จำนวนเหรียญขั้นต่ำสำหรับการแลกคือ ${EXCHANGE_RATE.MIN_WITHDRAWAL} T Coins`);
      return;
    }

    if (amount > getTotalReal()) {
      alert('จำนวนเหรียญไม่เพียงพอ');
      return;
    }

    setIsProcessing(true);
    try {
      // สร้างคำขอแลกเหรียญ
      await addDoc(collection(db, 'exchangeRequests'), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName,
        amount: amount,
        status: 'pending',
        createdAt: new Date(),
        fee: Math.max(amount * EXCHANGE_RATE.FEE_PERCENTAGE / 100, EXCHANGE_RATE.FEE_MINIMUM),
        netAmount: amount - Math.max(amount * EXCHANGE_RATE.FEE_PERCENTAGE / 100, EXCHANGE_RATE.FEE_MINIMUM)
      });

      alert('ส่งคำขอแลกเหรียญสำเร็จ! แอดมินจะตรวจสอบและติดต่อกลับ');
      setExchangeAmount('');
      loadExchangeHistory();
    } catch (error) {
      console.error('Error creating exchange request:', error);
      alert('เกิดข้อผิดพลาดในการส่งคำขอแลกเหรียญ');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รอยืนยัน';
      case 'approved': return 'อนุมัติแล้ว';
      case 'rejected': return 'ปฏิเสธ';
      default: return 'ไม่ทราบ';
    }
  };

  const exchangeValue = getExchangeValue();
  const totalReal = getTotalReal();

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">🔄 แลกเหรียญเป็นเงิน</h2>

      {/* สถานะเหรียญปัจจุบัน */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">T Coins ที่มี</p>
              <p className="text-2xl font-bold">{totalReal}</p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">เงินที่จะได้รับ</p>
              <p className="text-2xl font-bold">
                {exchangeValue.canExchange ? `${exchangeValue.net.toLocaleString()} บาท` : 'ไม่สามารถแลกได้'}
              </p>
            </div>
            <div className="text-4xl">💵</div>
          </div>
        </div>
      </div>

      {/* ข้อมูลการแลก */}
      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">ข้อมูลการแลกเหรียญ</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">อัตราแลกเปลี่ยน:</p>
            <p className="font-semibold">1 T Coin = 1 บาท</p>
          </div>
          <div>
            <p className="text-gray-600">ขั้นต่ำการแลก:</p>
            <p className="font-semibold">{EXCHANGE_RATE.MIN_WITHDRAWAL} T Coins</p>
          </div>
          <div>
            <p className="text-gray-600">ค่าธรรมเนียม:</p>
            <p className="font-semibold">{EXCHANGE_RATE.FEE_PERCENTAGE}% (ขั้นต่ำ {EXCHANGE_RATE.FEE_MINIMUM} บาท)</p>
          </div>
          <div>
            <p className="text-gray-600">เวลาดำเนินการ:</p>
            <p className="font-semibold">1-3 วันทำการ</p>
          </div>
        </div>
      </div>

      {/* ฟอร์มแลกเหรียญ */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">แลกเหรียญเป็นเงิน</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              จำนวน T Coins ที่ต้องการแลก
            </label>
            <input
              type="number"
              value={exchangeAmount}
              onChange={(e) => setExchangeAmount(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="จำนวนเหรียญ"
              min={EXCHANGE_RATE.MIN_WITHDRAWAL}
              max={totalReal}
            />
            <p className="text-xs text-gray-500 mt-1">
              ขั้นต่ำ {EXCHANGE_RATE.MIN_WITHDRAWAL} T Coins
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เงินที่จะได้รับ (ประมาณการ)
            </label>
            <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
              {exchangeAmount ? (
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-green-600">
                    {(parseInt(exchangeAmount) - Math.max(parseInt(exchangeAmount) * EXCHANGE_RATE.FEE_PERCENTAGE / 100, EXCHANGE_RATE.FEE_MINIMUM)).toLocaleString()} บาท
                  </p>
                  <p className="text-xs text-red-600">
                    (หักค่าธรรมเนียม {Math.max(parseInt(exchangeAmount) * EXCHANGE_RATE.FEE_PERCENTAGE / 100, EXCHANGE_RATE.FEE_MINIMUM)} บาท)
                  </p>
                </div>
              ) : (
                <p className="text-gray-500">กรอกจำนวนเหรียญเพื่อดูเงินที่จะได้รับ</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleExchangeRequest}
            disabled={isProcessing || !exchangeAmount || parseInt(exchangeAmount) < EXCHANGE_RATE.MIN_WITHDRAWAL || parseInt(exchangeAmount) > totalReal}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            {isProcessing ? 'กำลังส่งคำขอ...' : 'ส่งคำขอแลกเหรียญ'}
          </button>
        </div>
      </div>

      {/* ประวัติการแลกเหรียญ */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">ประวัติการแลกเหรียญ</h3>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
          >
            {showHistory ? 'ซ่อน' : 'แสดง'} ประวัติ
          </button>
        </div>

        {showHistory && (
          <>
            {exchangeHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>ไม่มีประวัติการแลกเหรียญ</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        จำนวนเหรียญ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        เงินที่จะได้รับ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        สถานะ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        วันที่ขอ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {exchangeHistory.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.amount} T Coins
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="space-y-1">
                            <p className="text-green-600 font-semibold">
                              {request.netAmount.toLocaleString()} บาท
                            </p>
                            <p className="text-red-600 text-xs">
                              (หักค่าธรรมเนียม {request.fee} บาท)
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                            {getStatusText(request.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {request.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || 'ไม่ทราบ'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* คำแนะนำ */}
      <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <h4 className="text-lg font-semibold text-blue-800 mb-2">💡 คำแนะนำ</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• การแลกเหรียญจะต้องได้รับการอนุมัติจากแอดมินก่อน</li>
          <li>• แอดมินจะติดต่อกลับภายใน 1-3 วันทำการ</li>
          <li>• เงินจะถูกโอนเข้าบัญชีธนาคารที่ระบุ</li>
          <li>• หากมีข้อสงสัยสามารถติดต่อแอดมินได้</li>
        </ul>
      </div>
    </div>
  );
};

export default CoinsExchange;
